import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Button,
  Chip,
  Divider,
  Snackbar,
  Alert,
  Stack,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import ShareIcon from "@mui/icons-material/Share";
import { QRCodeCanvas } from "qrcode.react";

const CustomerProfile = () => {
  const [snack, setSnack] = useState("");

  /* =========================
     FETCH USER FROM SESSION
     ========================= */
  const user = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("user")) || {};
    } catch {
      return {};
    }
  }, []);

  const referralUrl = `http://ritehomepros.com/refer-user/${user.referral_id}`;

  const initials = `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`.toUpperCase();

  /* =========================
     ACTIONS
     ========================= */
  const copyReferralLink = async () => {
    await navigator.clipboard.writeText(referralUrl);
    setSnack("Referral link copied to clipboard");
  };

  const downloadQR = () => {
    const canvas = document.getElementById("referral-qr");
    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");

    const link = document.createElement("a");
    link.href = pngUrl;
    link.download = `Referral-${user.referral_id}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const shareQR = async () => {
    const canvas = document.getElementById("referral-qr");
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/png")
    );

    const file = new File([blob], `Referral-${user.referral_id}.png`, {
      type: "image/png",
    });

    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({
        title: "Join using my referral",
        text: "Scan this QR to register under my referral",
        files: [file],
      });
    } else {
      setSnack("Sharing not supported on this device");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 5 }, background: "#f4f6fb", minHeight: "100vh" }}>
      <Grid container justifyContent="center">
        <Grid item xs={12} md={9} lg={7}>
          <Card
            sx={{
              borderRadius: 5,
              boxShadow: "0 20px 60px rgba(0,0,0,0.12)",
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              {/* ================= HEADER ================= */}
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Avatar
                  sx={{
                    width: 96,
                    height: 96,
                    mx: "auto",
                    mb: 2,
                    fontSize: 36,
                    fontWeight: 700,
                    background: "linear-gradient(135deg,#667eea,#764ba2)",
                  }}
                >
                  {initials}
                </Avatar>

                <Typography variant="h5" fontWeight={700}>
                  {user.full_name}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Customer Profile
                </Typography>
              </Box>

              {/* ================= USER DETAILS ================= */}
              <Box
                sx={{
                  background: "#f9fafb",
                  borderRadius: 4,
                  p: { xs: 2.5, md: 3 },
                  mb: 5,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{ mb: 3, color: "#111827" }}
                >
                  Personal Information
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      Full Name
                    </Typography>
                    <Typography fontWeight={600}>{user.full_name}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      Email Address
                    </Typography>
                    <Typography fontWeight={600}>{user.email}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      Phone Number
                    </Typography>
                    <Typography fontWeight={600}>{user.phone}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      ZIP Code
                    </Typography>
                    <Typography fontWeight={600}>{user.zip_code}</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Address
                    </Typography>
                    <Typography fontWeight={600}>{user.address}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* ================= REFERRAL SECTION ================= */}
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Refer & Grow Your Network
              </Typography>

              <Typography variant="body2" color="text.secondary" mb={3}>
                Share your referral link or QR code. Anyone joining through this
                will be registered under you.
              </Typography>

              <Grid container spacing={4} alignItems="center">
                {/* ACTION BUTTONS */}
                <Grid item xs={12} md={7}>
                  <Stack
                    direction="row"
                    spacing={2}
                    useFlexGap
                    flexWrap="wrap"
                  >
                    <Button
                      variant="contained"
                      startIcon={<ContentCopyIcon />}
                      onClick={copyReferralLink}
                      sx={{ borderRadius: 3 }}
                    >
                      Copy Link
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={downloadQR}
                      sx={{ borderRadius: 3 }}
                    >
                      Download QR
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<ShareIcon />}
                      onClick={shareQR}
                      sx={{ borderRadius: 3 }}
                    >
                      Share QR
                    </Button>
                  </Stack>
                </Grid>

                {/* QR CODE */}
                <Grid item xs={12} md={5} textAlign="center">
                  <QRCodeCanvas
                    id="referral-qr"
                    value={referralUrl}
                    size={160}
                  />
                  <Typography variant="caption" display="block" mt={1}>
                    Scan this QR to add a user under your reference
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ================= SNACKBAR ================= */}
      <Snackbar
        open={!!snack}
        autoHideDuration={2500}
        onClose={() => setSnack("")}
      >
        <Alert severity="success" variant="filled">
          {snack}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerProfile;
